import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Coins, UserPlus, History, MinusCircle, Settings2, Loader2 } from 'lucide-react';

export const CreditsTab = ({ sites, clients }) => {
  const [packages, setPackages] = useState([]);
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [form, setForm] = useState({ name: '', site_id: '', price: '', credits: '', description: '' });
  const [grantForm, setGrantForm] = useState({ user_id: '', site_id: '', amount: '', reason: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deductForm, setDeductForm] = useState({ amount: '', reason: '' });
  const [adjustForm, setAdjustForm] = useState({ amount: '' });
  const [showDeduct, setShowDeduct] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);

  const loadData = async () => {
    try {
      const [pkgRes, balRes, txRes] = await Promise.all([
        adminAPI.getCreditPackages(),
        adminAPI.getCreditBalances(),
        adminAPI.getCreditTransactions(),
      ]);
      setPackages(pkgRes.data);
      setBalances(balRes.data);
      setTransactions(txRes.data);
    } catch {}
  };

  useEffect(() => { loadData(); }, []);

  const createPackage = async () => {
    if (!form.name || !form.site_id || !form.price || !form.credits) {
      toast.error('Remplissez tous les champs obligatoires');
      return;
    }
    try {
      await adminAPI.createCreditPackage({
        name: form.name,
        site_id: form.site_id,
        price: parseFloat(form.price),
        credits: parseFloat(form.credits),
        description: form.description,
      });
      toast.success('Forfait créé');
      setShowCreate(false);
      setForm({ name: '', site_id: '', price: '', credits: '', description: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deletePackage = async (id) => {
    try {
      await adminAPI.deleteCreditPackage(id);
      toast.success('Forfait supprimé');
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const grantCredits = async () => {
    if (!grantForm.user_id || !grantForm.site_id || !grantForm.amount) {
      toast.error('Remplissez tous les champs obligatoires');
      return;
    }
    try {
      const res = await adminAPI.grantCredits({
        user_id: grantForm.user_id,
        site_id: grantForm.site_id,
        amount: parseFloat(grantForm.amount),
        reason: grantForm.reason,
      });
      toast.success(res.data.message);
      setShowGrant(false);
      setGrantForm({ user_id: '', site_id: '', amount: '', reason: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.city || siteId;

  const openUserDetail = async (userId, siteId) => {
    setSelectedUser({ userId, siteId });
    setLoadingDetail(true);
    try {
      const r = await adminAPI.getUserCredits(userId);
      setUserDetail(r.data);
    } catch { toast.error('Erreur chargement'); }
    setLoadingDetail(false);
  };

  const handleDeduct = async () => {
    if (!selectedUser || !deductForm.amount) return;
    try {
      await adminAPI.deductCredits({
        user_id: selectedUser.userId, site_id: selectedUser.siteId,
        amount: parseFloat(deductForm.amount), reason: deductForm.reason
      });
      toast.success('Credits retires');
      setShowDeduct(false);
      setDeductForm({ amount: '', reason: '' });
      openUserDetail(selectedUser.userId, selectedUser.siteId);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleAdjust = async () => {
    if (!selectedUser || adjustForm.amount === '') return;
    try {
      await adminAPI.setCreditsBalance({
        user_id: selectedUser.userId, site_id: selectedUser.siteId,
        amount: parseFloat(adjustForm.amount), reason: `Ajustement manuel`
      });
      toast.success('Solde ajuste');
      setShowAdjust(false);
      setAdjustForm({ amount: '' });
      openUserDetail(selectedUser.userId, selectedUser.siteId);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const getClientName = (userId) => {
    const c = clients.find(cl => cl.id === userId);
    return c ? `${c.name} (${c.email})` : userId;
  };

  return (
    <div className="space-y-8" data-testid="credits-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            <Coins className="w-5 h-5 text-[#00FF00]" /> Forfaits Crédits
          </h2>
          <p className="text-sm text-slate-500 mt-1">{packages.length} forfait(s) • {balances.length} client(s) avec crédits</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showHistory} onOpenChange={setShowHistory}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#2A2A2A] text-slate-300 hover:text-white" data-testid="credits-history-btn">
                <History className="w-4 h-4 mr-1" /> Historique
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Historique des crédits</DialogTitle></DialogHeader>
              <div className="space-y-2 mt-4">
                {transactions.length === 0 && <p className="text-slate-500 text-sm text-center py-4">Aucune transaction</p>}
                {transactions.slice(0, 50).map(tx => (
                  <div key={tx.id} className="flex justify-between items-center bg-[#0A0A0A] rounded-lg px-4 py-3 text-sm">
                    <div>
                      <p className="text-white font-medium">{getClientName(tx.user_id)}</p>
                      <p className="text-slate-500 text-xs">{tx.description} • {getSiteName(tx.site_id)}</p>
                      <p className="text-slate-600 text-xs">{new Date(tx.created_at).toLocaleString('fr-FR')}</p>
                    </div>
                    <span className={`font-bold ${tx.amount > 0 ? 'text-[#00FF00]' : 'text-red-400'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}€
                    </span>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showGrant} onOpenChange={setShowGrant}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#00FF00] text-[#00FF00] hover:bg-[#00FF00] hover:text-black" data-testid="grant-credits-btn">
                <UserPlus className="w-4 h-4 mr-1" /> Attribuer crédits
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
              <DialogHeader><DialogTitle>Attribuer des crédits</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300 text-sm">Client</Label>
                  <Select value={grantForm.user_id} onValueChange={v => setGrantForm(f => ({ ...f, user_id: v }))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1"><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                      {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-white">{c.name} ({c.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Site</Label>
                  <Select value={grantForm.site_id} onValueChange={v => setGrantForm(f => ({ ...f, site_id: v }))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                      {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-white">{s.city}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Montant (crédits/€)</Label>
                  <Input type="number" value={grantForm.amount} onChange={e => setGrantForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="100" className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="grant-amount-input" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Raison (optionnel)</Label>
                  <Input value={grantForm.reason} onChange={e => setGrantForm(f => ({ ...f, reason: e.target.value }))}
                    placeholder="Geste commercial..." className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" />
                </div>
                <Button onClick={grantCredits} className="w-full bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="confirm-grant-btn">
                  Attribuer les crédits
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="create-package-btn">
                <Plus className="w-4 h-4 mr-1" /> Nouveau forfait
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
              <DialogHeader><DialogTitle>Créer un forfait crédits</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300 text-sm">Nom du forfait</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Forfait Premium" className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="package-name-input" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Site</Label>
                  <Select value={form.site_id} onValueChange={v => setForm(f => ({ ...f, site_id: v }))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                      {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-white">{s.city}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 text-sm">Prix (€)</Label>
                    <Input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                      placeholder="500" className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="package-price-input" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Crédits offerts</Label>
                    <Input type="number" value={form.credits} onChange={e => setForm(f => ({ ...f, credits: e.target.value }))}
                      placeholder="600" className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="package-credits-input" />
                  </div>
                </div>
                {form.price && form.credits && parseFloat(form.price) > 0 && (
                  <p className="text-xs text-[#00FF00]">
                    Bonus : +{(parseFloat(form.credits) - parseFloat(form.price)).toFixed(0)}€ de crédits ({((parseFloat(form.credits) / parseFloat(form.price) - 1) * 100).toFixed(0)}% de bonus)
                  </p>
                )}
                <div>
                  <Label className="text-slate-300 text-sm">Description (optionnel)</Label>
                  <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="10% de crédits bonus" className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" />
                </div>
                <Button onClick={createPackage} className="w-full bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="confirm-create-package-btn">
                  Créer le forfait
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Packages list */}
      <div>
        <h3 className="font-heading text-base font-bold text-white mb-4">Forfaits disponibles</h3>
        {packages.length === 0 ? (
          <div className="text-center py-12 bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A]">
            <Coins className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-500">Aucun forfait créé</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map(pkg => (
              <div key={pkg.id} className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-5 hover:border-[#333] transition-colors" data-testid={`package-card-${pkg.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-heading text-base font-bold text-white">{pkg.name}</h4>
                    <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 text-xs mt-1">{getSiteName(pkg.site_id)}</Badge>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0" data-testid={`delete-package-${pkg.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce forfait ?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          Le forfait "{pkg.name}" sera définitivement supprimé. Les crédits déjà achetés par les clients ne seront pas affectés.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-[#0A0A0A] border-[#2A2A2A] text-white hover:bg-[#2A2A2A]">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deletePackage(pkg.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid={`confirm-delete-${pkg.id}`}>
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-heading text-2xl font-bold text-[#00FF00]">{pkg.credits}€</span>
                  <span className="text-sm text-slate-500">de crédits</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-lg font-bold text-white">{pkg.price}€</span>
                  <span className="text-xs text-slate-500">à payer</span>
                  {pkg.credits > pkg.price && (
                    <Badge className="bg-orange-500/15 text-orange-400 border-0 text-xs ml-auto">
                      +{((pkg.credits / pkg.price - 1) * 100).toFixed(0)}% bonus
                    </Badge>
                  )}
                </div>
                {pkg.description && <p className="text-xs text-slate-500 mt-2">{pkg.description}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Client balances */}
      <div>
        <h3 className="font-heading text-base font-bold text-white mb-4">Soldes clients</h3>
        {balances.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-8">Aucun client avec des crédits</p>
        ) : (
          <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2A2A2A]">
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Client</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Site</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium">Solde</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium">Acheté</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium">Utilisé</th>
                </tr>
              </thead>
              <tbody>
                {balances.map(b => (
                  <tr key={`${b.user_id}-${b.site_id}`}
                    className="border-b border-[#2A2A2A]/50 hover:bg-[#0A0A0A]/50 cursor-pointer"
                    onClick={() => openUserDetail(b.user_id, b.site_id)}
                    data-testid={`balance-row-${b.user_id}`}
                  >
                    <td className="px-4 py-3 text-white">{getClientName(b.user_id)}</td>
                    <td className="px-4 py-3 text-slate-400">{getSiteName(b.site_id)}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#00FF00]">{b.balance}EUR</td>
                    <td className="px-4 py-3 text-right text-slate-400">{b.total_purchased || 0}EUR</td>
                    <td className="px-4 py-3 text-right text-slate-400">{b.total_used || 0}EUR</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Client detail dialog */}
      <Dialog open={!!selectedUser} onOpenChange={(open) => { if (!open) { setSelectedUser(null); setUserDetail(null); } }}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              {selectedUser && getClientName(selectedUser.userId)} — Credits
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedUser && getSiteName(selectedUser.siteId)}
            </DialogDescription>
          </DialogHeader>

          {loadingDetail ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00FF00]" /></div>
          ) : userDetail && (
            <div className="space-y-5 mt-2">
              {/* Balance summary */}
              <div className="grid grid-cols-3 gap-3">
                {userDetail.balances.filter(b => b.site_id === selectedUser?.siteId).map(b => (
                  <React.Fragment key={b.site_id}>
                    <div className="bg-[#0A0A0A] rounded-xl p-3 text-center border border-[#2A2A2A]">
                      <p className="text-[10px] text-slate-500 uppercase">Solde</p>
                      <p className="text-xl font-bold text-[#00FF00]">{b.balance}EUR</p>
                    </div>
                    <div className="bg-[#0A0A0A] rounded-xl p-3 text-center border border-[#2A2A2A]">
                      <p className="text-[10px] text-slate-500 uppercase">Achete</p>
                      <p className="text-xl font-bold text-white">{b.total_purchased || 0}EUR</p>
                    </div>
                    <div className="bg-[#0A0A0A] rounded-xl p-3 text-center border border-[#2A2A2A]">
                      <p className="text-[10px] text-slate-500 uppercase">Utilise</p>
                      <p className="text-xl font-bold text-orange-400">{b.total_used || 0}EUR</p>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button onClick={() => { setDeductForm({ amount: '', reason: '' }); setShowDeduct(true); }}
                  className="flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 text-xs" data-testid="deduct-credits-btn">
                  <MinusCircle className="w-3.5 h-3.5 mr-1" /> Retirer
                </Button>
                <Button onClick={() => {
                    const bal = userDetail.balances.find(b => b.site_id === selectedUser?.siteId);
                    setAdjustForm({ amount: bal?.balance || 0 }); setShowAdjust(true);
                  }}
                  className="flex-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30 text-xs" data-testid="adjust-credits-btn">
                  <Settings2 className="w-3.5 h-3.5 mr-1" /> Ajuster le solde
                </Button>
              </div>

              {/* Deduct form */}
              {showDeduct && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-bold text-red-400 uppercase">Retirer des credits</h4>
                  <Input type="number" placeholder="Montant" value={deductForm.amount} onChange={e => setDeductForm({...deductForm, amount: e.target.value})}
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="deduct-amount" />
                  <Input placeholder="Raison (optionnel)" value={deductForm.reason} onChange={e => setDeductForm({...deductForm, reason: e.target.value})}
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="deduct-reason" />
                  <div className="flex gap-2">
                    <Button onClick={handleDeduct} className="flex-1 bg-red-600 text-white hover:bg-red-700 text-xs font-bold" data-testid="confirm-deduct-btn">Confirmer le retrait</Button>
                    <Button variant="ghost" onClick={() => setShowDeduct(false)} className="text-slate-400 text-xs">Annuler</Button>
                  </div>
                </div>
              )}

              {/* Adjust form */}
              {showAdjust && (
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-bold text-blue-400 uppercase">Ajuster le solde</h4>
                  <Input type="number" placeholder="Nouveau solde" value={adjustForm.amount} onChange={e => setAdjustForm({...adjustForm, amount: e.target.value})}
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="adjust-amount" />
                  <div className="flex gap-2">
                    <Button onClick={handleAdjust} className="flex-1 bg-blue-600 text-white hover:bg-blue-700 text-xs font-bold" data-testid="confirm-adjust-btn">Confirmer</Button>
                    <Button variant="ghost" onClick={() => setShowAdjust(false)} className="text-slate-400 text-xs">Annuler</Button>
                  </div>
                </div>
              )}

              {/* Sessions paid with credits */}
              {userDetail.credit_bookings.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">Sessions payees par credits</h4>
                  <div className="space-y-2">
                    {userDetail.credit_bookings.map(bk => (
                      <div key={bk.id} className="flex items-center justify-between bg-[#0A0A0A] rounded-lg p-3 border border-[#2A2A2A]">
                        <div>
                          <p className="text-xs font-medium text-white">{bk.activity_name}</p>
                          <p className="text-[10px] text-slate-500">{bk.date} {bk.time_slot}</p>
                        </div>
                        <Badge className="bg-orange-500/15 text-orange-400 border-0 text-[10px]">-{bk.price}EUR</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transaction history */}
              <div>
                <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">Historique des transactions</h4>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {userDetail.transactions.length === 0 && <p className="text-xs text-slate-600 text-center py-4">Aucune transaction</p>}
                  {userDetail.transactions.map(tx => (
                    <div key={tx.id} className="flex items-center justify-between bg-[#0A0A0A] rounded-lg px-3 py-2 border border-[#2A2A2A]">
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] text-slate-400 truncate">{tx.description || tx.type}</p>
                        <p className="text-[10px] text-slate-600">{tx.created_at?.split('T')[0]}</p>
                      </div>
                      <span className={`text-xs font-bold whitespace-nowrap ml-2 ${tx.amount >= 0 ? 'text-[#00FF00]' : 'text-red-400'}`}>
                        {tx.amount >= 0 ? '+' : ''}{tx.amount}EUR
                      </span>
                      <span className="text-[10px] text-slate-500 ml-2 whitespace-nowrap">={tx.balance_after}EUR</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
